import ApiManager from "./ApiManager";
import { AppCache } from "./myStorage";
import { AkeadSignature256 } from "./signature";
import { base64UrlToBase64, decodeBase64Utf8, encodeUtf8Base64 } from "./base64Utils";
import { getServiceToken2 } from "./myStorage";
import config from "../config";
import { CryptoJSAesEncrypt } from "./cryp";

const partnerUrl = config().PARTNER_URL;
const partnerProdUrl = config().PARTNER_URL;
// const partnerUrl = process.env.REACT_APP_PARTNER_URL;
// const countryUrl = process.env.REACT_APP_LIST_URL;
const countryUrl = config().LIST_URL;

export default class PartnerApi extends ApiManager {

    static async getCountryList(params = {}) {
        const url = new URL(`${countryUrl}/countryList`);
        url.searchParams.append("language", params.language || "tr");
        url.searchParams.append("status", params.status ?? 1);
        url.searchParams.append("limit", params.limit ?? 10);
        url.searchParams.append("offset", params.offset ?? 0);

        Object.entries(params).forEach(([key, value]) => {
            if (!["language", "status", "limit", "offset"].includes(key)) {
                url.searchParams.append(key, value);
            }
        });

        const urlString = url.toString();

        const cachedData = this.getCachedRequest(urlString);
        if (cachedData) return cachedData;

        const accessToken = AppCache.getApplicationJwt();
        const lstToken = AppCache.getServiceJWT(554);
        const jwtToken = accessToken && lstToken ? `${accessToken}.${lstToken}` : "";

        const response = await fetch(urlString, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("countryList fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const jsonData = await response.json();
        this.setCachedRequest(urlString, jsonData);
        return jsonData;
    }
    static async getPartnerList(params = {}) {
        const url = new URL(`${partnerUrl}/admin/partnerList`);
        url.searchParams.append("status", params.status ?? 1);

        Object.entries(params).forEach(([key, value]) => {
            if (!["status"].includes(key)) {
                url.searchParams.append(key, value);
            }
        });

        const urlString = url.toString();

        const cachedData = this.getCachedRequest(urlString);
        if (cachedData) return cachedData;

        const accessToken = AppCache.getApplicationJwt();
        const partnerToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && partnerToken ? `${accessToken}.${partnerToken}` : "";

        const response = await fetch(urlString, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("partnerList fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const jsonData = await response.json();
        this.setCachedRequest(urlString, jsonData);
        return jsonData;
    }
    static async getPartnerUserList() {
        try {
            const urlString = `${partnerUrl}/admin/partnerUserList`;

            const accessToken = AppCache.getApplicationJwt();
            const partnerToken = AppCache.getServiceJWT(502);
            const jwtToken = accessToken && partnerToken ? `${accessToken}.${partnerToken}` : "";

            const response = await fetch(urlString, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection() || "",
                    jwt: jwtToken,
                },
            });

            if (!response.ok) {
                const errText = await response.text();
                console.error("partnerUserList fetch error:", response.status, errText);
                throw new Error(errText || response.statusText);
            }

            const jsonData = await response.json();
            return jsonData.partnerUserList || [];
        } catch (error) {
            console.error("getPartnerUserList error:", error);
            return [];
        }
    }
    static async getLicenseSalesList({ licenseNo, startDate = "", finishDate = "" }) {
        try {
            const url = new URL(`${partnerUrl}/admin/licenseSalesList`);
            url.searchParams.append("licenseNo", licenseNo);
            if (startDate) url.searchParams.append("startDate", startDate);
            if (finishDate) url.searchParams.append("finishDate", finishDate);

            const accessToken = AppCache.getApplicationJwt();
            const partnerToken = AppCache.getServiceJWT(502);
            const jwtToken = accessToken && partnerToken ? `${accessToken}.${partnerToken}` : "";

            const response = await fetch(url.toString(), {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection(),
                    jwt: jwtToken,
                },
            });

            if (!response.ok) {
                const errText = await response.text();
                console.error("licenseSalesList fetch error:", response.status, errText);
                throw new Error(errText || response.statusText);
            }

            const jsonData = await response.json();
            return jsonData.licenseSalesList || [];
        } catch (error) {
            console.error("getLicenseSalesList error:", error);
            return [];
        }
    }
    static async getLicenseBackupLogList(licenseNo) {
        try {
            const url = new URL(`${partnerUrl}/admin/licenseBackupLogList`);
            url.searchParams.append("licenseNo", licenseNo);

            const accessToken = AppCache.getApplicationJwt();
            const partnerToken = AppCache.getServiceJWT(502);
            const jwtToken = accessToken && partnerToken ? `${accessToken}.${partnerToken}` : "";

            const response = await fetch(url.toString(), {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection(),
                    jwt: jwtToken,
                },
            });

            if (!response.ok) {
                const errText = await response.text();
                console.error("licenseBackupLogList fetch error:", response.status, errText);
                throw new Error(errText || response.statusText);
            }

            const jsonData = await response.json();
            return jsonData.licenseBackupLogList || [];
        } catch (error) {
            console.error("licenseBackupLogList error:", error);
            return [];
        }
    }
    static async getProductList(params = {}) {
        const url = new URL(`${partnerUrl}/admin/productList`);
        url.searchParams.append("language", params.language || "tr");
        url.searchParams.append("isPrincipal", params.isPrincipal ?? "true");
        url.searchParams.append("isFileName", params.isFileName ?? "true");

        const urlString = url.toString();

        const accessToken = AppCache.getApplicationJwt();
        const partnerToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && partnerToken ? `${accessToken}.${partnerToken}` : "";

        const response = await fetch(urlString, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("productList fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const jsonData = await response.json();
        return jsonData;
    }
    static async getModuleList(params = {}) {
        const url = new URL(`${partnerUrl}/admin/moduleList`);
        url.searchParams.append("language", params.language || "tr");

        Object.entries(params).forEach(([key, value]) => {
            if (!["language"].includes(key)) {
                url.searchParams.append(key, value);
            }
        });

        const urlString = url.toString();

        const cachedData = this.getCachedRequest(urlString);
        if (cachedData) return cachedData;

        const accessToken = AppCache.getApplicationJwt();
        const moduleToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && moduleToken ? `${accessToken}.${moduleToken}` : "";

        const response = await fetch(urlString, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("moduleList fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const jsonData = await response.json();
        this.setCachedRequest(urlString, jsonData);
        return jsonData;
    }


    static async getSectorList(params = {}) {
        const url = new URL(`${partnerUrl}/admin/sectorList`);
        url.searchParams.append("language", params.language || "tr");

        Object.entries(params).forEach(([key, value]) => {
            if (!["language"].includes(key)) {
                url.searchParams.append(key, value);
            }
        });

        const urlString = url.toString();

        const cachedData = this.getCachedRequest(urlString);
        if (cachedData) return cachedData;

        const accessToken = AppCache.getApplicationJwt();
        const moduleToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && moduleToken ? `${accessToken}.${moduleToken}` : "";

        const response = await fetch(urlString, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("moduleList fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const jsonData = await response.json();
        this.setCachedRequest(urlString, jsonData);
        return jsonData;
    }
    static async getSupportTypeList(params = {}) {
        const url = new URL(`${partnerUrl}/admin/supportTypeList`);
        url.searchParams.append("language", params.language || "tr");

        Object.entries(params).forEach(([key, value]) => {
            if (!["language"].includes(key)) {
                url.searchParams.append(key, value);
            }
        });

        const urlString = url.toString();

        const cachedData = this.getCachedRequest(urlString);
        if (cachedData) return cachedData;

        const accessToken = AppCache.getApplicationJwt();
        const moduleToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && moduleToken ? `${accessToken}.${moduleToken}` : "";

        const response = await fetch(urlString, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("moduleList fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const jsonData = await response.json();
        this.setCachedRequest(urlString, jsonData);
        return jsonData;
    }
    static async getLicenseFilterList(params = {}) {
        const url = new URL(`${partnerUrl}/admin/licenseFilterList`);
        url.searchParams.append("language", params.language || "tr");

        Object.entries(params).forEach(([key, value]) => {
            if (!["language"].includes(key)) {
                url.searchParams.append(key, value);
            }
        });

        const urlString = url.toString();

        const cachedData = this.getCachedRequest(urlString);
        if (cachedData) return cachedData;

        const accessToken = AppCache.getApplicationJwt();
        const moduleToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && moduleToken ? `${accessToken}.${moduleToken}` : "";

        const response = await fetch(urlString, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("moduleList fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const jsonData = await response.json();
        this.setCachedRequest(urlString, jsonData);
        return jsonData;
    }
    // static async postActivationRecordByFile(params = {}) {
    //     const url = new URL(`${partnerUrl}/license/activationRecordByFile`);
    //     const { licenseNo, productCode, file } = params;

    //     const postData = {
    //         licenseNo,
    //         productCode: String(productCode),
    //         file: file,
    //     };

    //     const rawSignature = licenseNo + productCode;
    //     const signature = AkeadSignature256(rawSignature);

    //     const response = await fetch(url.toString(), {
    //         method: "POST",
    //         headers: {
    //             Accept: "application/json",
    //             "Content-Type": "application/json",
    //             signature: signature,
    //         },
    //         body: JSON.stringify(postData),
    //     });

    //     if (!response.ok) {
    //         const errorText = await response.text();
    //         throw new Error(errorText);
    //     }

    //     return await response.json();
    // }
//   static async postActivationRecordByFile(params = {}) {
//  const url = new URL(`${config().PARTNER_URL}/license/activationRecordByFile`);

//   const licenseNo = String(params.licenseNo).trim();
//   const productCode = String(params.productCode).trim();
//   const file = String(params.file).trim();

//   const postData = {
//     licenseNo:licenseNo,
//     productCode:productCode,
//     file:file,
//   };

//   const rawSignature = `${licenseNo}${productCode}`;
//   const signature = CryptoJSAesEncrypt(rawSignature, config().CHIPER_KEY);
 
//   console.log("ACTIVATION RAW SIGNATURE:", rawSignature);
//   console.log("ACTIVATION SIGNATURE:", signature);

//   const response = await fetch(url.toString(), {
//     method: "POST",
//     headers: {
//       Accept: "application/json",
//       "Content-Type": "application/json",
//       "Signature": signature
//     },
//     body: JSON.stringify(postData),
//   });

//   const responseText = await response.text();

//   if (!response.ok) {
//     throw new Error(responseText);
//   }

//   return JSON.parse(responseText);
// }
static async postActivationRecordByFile(params = {}) {
  const url = new URL(
    `${config().PARTNER_URL_LIVE}/license/activationRecordByFile`
  );

  const { licenseNo, productCode, file } = params;

  const postData = {
    licenseNo,
    productCode: String(productCode),
    file,
  };

  const rawSignature = licenseNo + productCode;

  const signature = CryptoJSAesEncrypt(
    rawSignature,
    config().LIVE_CHIPER_KEY
  );

  const response = await fetch(url.toString(), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      signature,
    },
    body: JSON.stringify(postData),
  });

  const responseText = await response.text();

  console.log("ACTIVATION RESPONSE:", response.status, responseText);

  if (!response.ok) {
    throw new Error(responseText);
  }

  return responseText ? JSON.parse(responseText) : null;
}

    static async getlicenseToBeApprovedList(params = {}) {
        try {
            const url = new URL(`${partnerUrl}/admin/licenseToBeApprovedList`);

            // resellerId varsa ekle, yoksa EKLEME -> böylece API "tüm liste" dönebilir
            if (params.resellerId !== undefined && params.resellerId !== null && params.resellerId !== "") {
                url.searchParams.append("resellerId", String(params.resellerId));
            }

            // owner opsiyonel
            if (params.owner && String(params.owner).trim() !== "") {
                url.searchParams.append("owner", String(params.owner).trim());
            }

            const accessToken = AppCache.getApplicationJwt();
            const lstToken = AppCache.getServiceJWT(502);
            const jwtToken = accessToken && lstToken ? `${accessToken}.${lstToken}` : "";

            const response = await fetch(url.toString(), {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection() || "",
                    jwt: jwtToken,
                },
            });

            if (!response.ok) {
                let detail = "";
                try { detail = await response.text(); } catch (_) { }
                throw new Error(`licenseToBeApprovedList API hatası: ${response.status}${detail ? ` - ${detail}` : ""}`);
            }

            const jsonData = await response.json();
            return {
                licenseToBeApprovedList: Array.isArray(jsonData?.licenseToBeApprovedList) ? jsonData.licenseToBeApprovedList : [],
                ...jsonData,
            };
        } catch (error) {
            console.error("getlicenseToBeApprovedList error:", error);
            return { licenseToBeApprovedList: [] };
        }
    }
    static async postSpecialCaseRecord(params = {}) {
        try {
            const url = `${partnerUrl}/admin/specialCaseRecord`;

            const body = {
                partnerSalesId: params.partnerSalesId,
                note: params.note,
                salesList: params.salesList,
            };

            const accessToken = AppCache.getApplicationJwt();
            const lstToken = AppCache.getServiceJWT(502);
            const jwtToken = accessToken && lstToken ? `${accessToken}.${lstToken}` : "";

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection() || "",
                    jwt: jwtToken,
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) throw new Error("API hatası: " + response.status);

            const jsonData = await response.json();
            return jsonData;
        } catch (error) {
            console.error("postSpecialCaseRecord error:", error);
            return null;
        }
    }
    static async onSaveNote({ noteText, selectedPartnerSalesId, selectedItems, setShowModal, setNoteText }) {
        try {
            const trimmedNote = noteText?.trim();

            if (!trimmedNote || !selectedPartnerSalesId || !selectedItems?.length) {
                alert("Lütfen tüm gerekli alanları doldurun.");
                return;
            }

            const params = {
                note: trimmedNote,
                partnerSalesId: selectedPartnerSalesId,
                salesList: selectedItems.map(item => ({
                    salesId: item.salesId,
                    licenseNo: item.licenseNo,
                })),
            };

            const response = await PartnerApi.postSpecialCaseRecord(params);

            if (response?.success === "true") {
                alert("Özel durum başarıyla kaydedildi.");
                if (setShowModal) setShowModal(false);
                if (setNoteText) setNoteText("");
            } else {
                console.warn("İşlem başarısız:", response);
                alert("İşlem başarısız: " + (response?.message || "Sunucu hatası"));
            }
        } catch (error) {
            console.error("Sunucu hatası:", error);
            alert("Sunucu hatası: " + error.message);
        }
    }
    static async postCancelSales(params = {}) {
        const { partnerResellerId, note, salesList } = params;
        const url = `${partnerUrl}/admin/cancelSales`;
        const body = {
            note: note?.trim() || "Default note",
            partnerResellerId,
            salesList,
        };

        const accessToken = AppCache.getApplicationJwt();
        const lstToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && lstToken ? `${accessToken}.${lstToken}` : "";

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection() || "",
                    jwt: jwtToken,
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorData = null;
                try {
                    errorData = JSON.parse(errorText);
                } catch { }
                console.error("API error response text:", errorText);

                if (errorData?.error) {
                    throw errorData.error;
                }

                throw { code: response.status, message: response.statusText };
            }

            return await response.json(); // { success: true } bekleniyor
        } catch (error) {
            console.error("postCancelSales error:", error);
            throw error;
        }
    }
    static async postApproveSales(params = {}) {
        const { partnerResellerId, note, salesList } = params;
        const url = `${partnerUrl}/admin/approveSales`;

        const body = {
            note: note?.trim() || "Default note",
            partnerResellerId,
            salesList,
        };

        const accessToken = AppCache.getApplicationJwt();
        const lstToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && lstToken ? `${accessToken}.${lstToken}` : "";

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection() || "",
                    jwt: jwtToken,
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorData = null;
                try {
                    errorData = JSON.parse(errorText);
                } catch { }

                console.error("API error response text:", errorText);

                // JSON hata nesnesi varsa direkt fırlat
                if (errorData?.error) {
                    throw errorData.error; // { code, message, more }
                }

                throw { code: response.status, message: response.statusText };
            }

            return await response.json(); // { success: true } bekleniyor
        } catch (error) {
            console.error("postApproveSales error:", error);
            throw error; // null dönme, hata nesnesini fırlat
        }
    }
    static async getPackList({ language = "tr", status, productCode } = {}) {
        const url = new URL(`${partnerUrl}/admin/packList`);

        if (language) url.searchParams.append("language", language);
        if (status !== undefined) url.searchParams.append("status", status);
        if (productCode) url.searchParams.append("productCode", productCode);

        const accessToken = AppCache.getApplicationJwt();
        const lstToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && lstToken ? `${accessToken}.${lstToken}` : "";

        try {
            const response = await fetch(url.toString(), {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection() || "",
                    jwt: jwtToken,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorData = null;
                try {
                    errorData = JSON.parse(errorText);
                } catch { }
                console.error("API error response text:", errorText);
                throw new Error(
                    `API hatası: ${response.status} - ${errorData?.error?.message || errorText || response.statusText
                    }`
                );
            }

            return await response.json(); // { packList: [...] }
        } catch (error) {
            console.error("getPackList error:", error);
            return null;
        }
    }
    static async getSalesModuleList(params = {}) {
        if (!params.resellerId || !params.productPackId) {
            console.warn("resellerId veya productPackId eksik, salesModuleList çağrısı yapılmadı.");
            return [];
        }

        const url = new URL(`${partnerUrl}/admin/salesModuleList`);
        url.searchParams.append("language", params.language || "tr");
        url.searchParams.append("resellerId", params.resellerId);
        url.searchParams.append("productPackId", params.productPackId);

        const urlString = url.toString();
        const accessToken = AppCache.getApplicationJwt();
        const partnerToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && partnerToken ? `${accessToken}.${partnerToken}` : "";

        const response = await fetch(urlString, {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("salesModuleList fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            throw new Error("Invalid response format (expected JSON)");
        }

        const jsonData = await response.json();
        return jsonData?.salesModuleList || [];
    }
    static async postLicenseNote(params = {}) {
        const { licenseId, note, internal = 0 } = params;

        if (licenseId == null || licenseId === "") {
            throw new Error("licenseId is required");
        }

        const base = String(partnerUrl || "").replace(/\/+$/, "");
        const url = `${base}/admin/licenseNote`;

        const internalNum =
            typeof internal === "boolean" ? (internal ? 1 : 0) : Number(internal) ? 1 : 0;

        const body = {
            licenseId,
            note: (note || "").trim(),
            internal: internalNum,
        };

        const accessToken = AppCache.getApplicationJwt();
        const lstToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && lstToken ? `${accessToken}.${lstToken}` : "";

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection() || "",
                    jwt: jwtToken,
                },
                body: JSON.stringify(body),
            });

            const text = await response.text(); // 200 ise genelde JSON, ama yine de korumalı
            let data = null;
            try { data = text ? JSON.parse(text) : null; } catch { }

            if (!response.ok) {
                console.error("postLicenseNote failed", {
                    url,
                    status: response.status,
                    statusText: response.statusText,
                    body,
                    responseText: text,
                });
                if (data?.error) throw data.error;
                throw { code: response.status, message: response.statusText };
            }

            const createdId = data?.licenseNote?.id ?? data?.id ?? null;
            const successRaw = data?.success;
            const success = Boolean(createdId) || successRaw === true || successRaw === "true" || successRaw === 1 || successRaw === "1";

            return {
                success,
                licenseNoteId: createdId,
                raw: data,
            };
        } catch (error) {
            console.error("postLicenseNote error:", error);
            throw error;
        }
    }
    static async deleteLicenseNote(id) {
        if (id == null || id === "") throw new Error("Note id is required");

        const base = String(partnerUrl || "").replace(/\/+$/, "");
        const url = `${base}/admin/licenseNote?${new URLSearchParams({ id: String(id) })}`;

        const accessToken = AppCache.getApplicationJwt();
        const lstToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && lstToken ? `${accessToken}.${lstToken}` : "";

        const res = await fetch(url, {
            method: "DELETE",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "connection-code": AppCache.getSelectedConnection() || "",
                jwt: jwtToken,
            },
        });

        const text = await res.text();
        let data = null;
        try { data = text ? JSON.parse(text) : null; } catch { }

        if (!res.ok) {
            console.error("DELETE /admin/licenseNote failed", { url, status: res.status, text });
            if (data?.error) throw data.error; // { code, message, ... }
            throw { code: res.status, message: res.statusText };
        }

        // API: { "success": "true" } veya boş body (204) olabilir
        const successRaw = data?.success;
        const success = successRaw === true || successRaw === "true" || successRaw === 1 || successRaw === "1" || data == null;

        return { success, raw: data };
    }
    static async postSignLicenseAgreement(params = {}) {
        const { licenseNo } = params;
        const url = `${partnerUrl}/admin/signLicenseAgreement`;
        const body = { licenseNo };

        const accessToken = AppCache.getApplicationJwt();
        const lstToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && lstToken ? `${accessToken}.${lstToken}` : "";

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    "connection-code": AppCache.getSelectedConnection() || "",
                    jwt: jwtToken,
                },
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorData = null;

                try {
                    errorData = JSON.parse(errorText);
                } catch { }

                console.error("API error response text:", errorText);

                if (errorData?.error) {
                    throw errorData.error;
                }

                throw { code: response.status, message: response.statusText };
            }
            return await response.json();
        } catch (error) {
            console.error("postSignLicenseAgreement error:", error);
            throw error;
        }
    }
    static async calculateSalesPrice(body = {}) {
        // Basit zorunlu alan kontrolü
        const missing = [];
        if (!body?.productPackId) missing.push("productPackId");
        if (!body?.resellerId) missing.push("resellerId");
        if (!body?.numberOfUser) missing.push("numberOfUser");
        if (!body?.numberOfDatabase) missing.push("numberOfDatabase");
        if (!Array.isArray(body?.moduleList) || body.moduleList.length === 0) missing.push("moduleList");

        if (missing.length > 0) {
            console.warn(
                `calculateSalesPrice çağrısı yapılmadı. Eksik alan(lar): ${missing.join(", ")}`
            );
            return null;
        }

        const url = new URL(`${partnerUrl}/admin/calculateSalesPrice`);
        const urlString = url.toString();

        const accessToken = AppCache.getApplicationJwt();
        const partnerToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && partnerToken ? `${accessToken}.${partnerToken}` : "";

        const response = await fetch(urlString, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
            body: JSON.stringify({
                numberOfUser: Number(body.numberOfUser),
                numberOfDatabase: Number(body.numberOfDatabase),
                language: body.language || "tr",
                productPackId: Number(body.productPackId),
                resellerId: Number(body.resellerId),
                moduleList: (body.moduleList || []).map((m) => {
                    // schema: { code, number?, month? }
                    const item = { code: m.code };
                    if (m.number !== undefined && m.number !== null && m.number !== "")
                        item.number = Number(m.number);
                    if (m.month !== undefined && m.month !== null && m.month !== "")
                        item.month = Number(m.month);
                    return item;
                }),
            }),
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("calculateSalesPrice fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            throw new Error("Invalid response format (expected JSON)");
        }

        const jsonData = await response.json();
        // response: { calculatePrice: {...} }
        return jsonData?.calculatePrice || jsonData;
    }
    static async postGenerateLicense(payload = {}) {
        const urlString = `${partnerUrl}/admin/generateLicense`;
        const accessToken = AppCache.getApplicationJwt();
        const partnerToken = AppCache.getServiceJWT(502);
        const jwtToken = accessToken && partnerToken ? `${accessToken}.${partnerToken}` : "";

        const response = await fetch(urlString, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "Connection-code": AppCache.getSelectedConnection(),
                jwt: jwtToken,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errText = await response.text();
            console.error("generateLicense fetch error:", response.status, errText);
            throw new Error(errText || response.statusText);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            throw new Error("Invalid response format (expected JSON)");
        }

        const jsonData = await response.json();
        return jsonData;
    }

}






